/**
 * getEffectiveProducerLocation
 *
 * Resolves the canonical visitor-facing destination for a producer.
 *
 * Precedence:
 * 1. Producer override fields (when address_override_enabled = true)
 *    → alternative meeting point / parking / entrance
 * 2. Producer primary address/location fields
 *    → the actual farm / host / visit destination
 * 3. Linked place (fallback / contextual)
 *    → editorial area anchor, NOT the exact destination
 *
 * Key insight: Place = contextual area anchor. Producer = actual destination.
 * Producer coordinates should be more exact than place coordinates.
 */

export interface EffectiveLocation {
  addressText: string | null;
  city: string | null;
  region: string | null;
  lat: number | null;
  lng: number | null;
  source: 'override' | 'producer' | 'place';
}

interface ProducerLocationFields {
  address_override_enabled?: boolean;
  address_override_text?: string | null;
  municipality_override?: string | null;
  region_override?: string | null;
  lat_override?: number | null;
  lng_override?: number | null;
  // Primary producer destination fields
  address_text?: string | null;
  city?: string | null;
  region?: string | null;
  lat?: number | null;
  lng?: number | null;
}

interface PlaceLocationFields {
  address_text?: string | null;
  municipality?: string | null;
  landskap?: string | null;
  lat?: number | null;
  lng?: number | null;
}

/** Check if producer has any meaningful primary location data */
function hasProducerLocation(p: ProducerLocationFields): boolean {
  return !!(p.address_text || p.city || (p.lat != null && p.lng != null));
}

export function getEffectiveProducerLocation(
  producer: ProducerLocationFields | null | undefined,
  linkedPlace?: PlaceLocationFields | null,
): EffectiveLocation {
  if (!producer) {
    return { addressText: null, city: null, region: null, lat: null, lng: null, source: 'producer' };
  }

  // 1. Producer override (alternative meeting point)
  if (producer.address_override_enabled) {
    return {
      addressText: producer.address_override_text || null,
      city: producer.municipality_override || null,
      region: producer.region_override || null,
      lat: producer.lat_override ?? null,
      lng: producer.lng_override ?? null,
      source: 'override',
    };
  }

  // 2. Producer primary destination (the normal case)
  if (hasProducerLocation(producer)) {
    return {
      addressText: producer.address_text || null,
      city: producer.city || null,
      region: producer.region || null,
      lat: producer.lat ?? null,
      lng: producer.lng ?? null,
      source: 'producer',
    };
  }

  // 3. Linked place as contextual fallback
  if (linkedPlace) {
    return {
      addressText: linkedPlace.address_text || null,
      city: linkedPlace.municipality || null,
      region: linkedPlace.landskap || null,
      lat: linkedPlace.lat ?? null,
      lng: linkedPlace.lng ?? null,
      source: 'place',
    };
  }

  // Nothing available
  return {
    addressText: producer.address_text || null,
    city: producer.city || null,
    region: producer.region || null,
    lat: producer.lat ?? null,
    lng: producer.lng ?? null,
    source: 'producer',
  };
}

/** Build a Google Maps URL from effective location */
export function buildGoogleMapsUrl(
  location: EffectiveLocation,
  producerName?: string,
): string {
  if (location.lat && location.lng) {
    return `https://www.google.com/maps?q=${location.lat},${location.lng}`;
  }
  const query = location.addressText || producerName || '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}
